import { fetchBlock, reorderBlocks, reparentBlock } from '@/lib/blocks/api';
import type { BlockRecord } from '@/lib/blocks/types';

export type SidebarDropTarget =
  | { kind: 'before'; parentId: string; index: number }
  | { kind: 'inside'; parentId: string };

export function sortByPosition(blocks: BlockRecord[]): BlockRecord[] {
  return [...blocks].sort((a, b) => a.position - b.position);
}

export function isDescendantInTree(
  dragId: string,
  ancestorId: string,
  childrenByParent: Record<string, BlockRecord[]>,
): boolean {
  const stack = [ancestorId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (id === dragId) {
      return true;
    }
    for (const child of childrenByParent[id] ?? []) {
      if (child.type === 'page' || child.type === 'database') {
        stack.push(child.id);
      }
    }
  }
  return false;
}

export function findParentOfBlock(
  blockId: string,
  childrenByParent: Record<string, BlockRecord[]>,
): string | null {
  for (const [parentId, kids] of Object.entries(childrenByParent)) {
    if (kids.some((b) => b.id === blockId)) {
      return parentId;
    }
  }
  return null;
}

export function applyOptimisticSidebarMove(
  childrenByParent: Record<string, BlockRecord[]>,
  blockId: string,
  target: SidebarDropTarget,
): {
  next: Record<string, BlockRecord[]>;
  oldParentId: string;
  newParentId: string;
} {
  const oldParentId = findParentOfBlock(blockId, childrenByParent);
  if (!oldParentId) {
    throw new Error('Block not in sidebar cache');
  }
  const dragged = (childrenByParent[oldParentId] ?? []).find((b) => b.id === blockId);
  if (!dragged) {
    throw new Error('Block not found');
  }

  const { parentId: newParentId, index: targetIndex } = resolveDropTarget(target, childrenByParent);
  const next: Record<string, BlockRecord[]> = { ...childrenByParent };

  next[oldParentId] = sortByPosition(next[oldParentId] ?? [])
    .filter((b) => b.id !== blockId)
    .map((b, i) => ({ ...b, position: i }));

  const siblings = sortByPosition(next[newParentId] ?? []).filter((b) => b.id !== blockId);
  const insertIndex = Math.max(0, Math.min(targetIndex, siblings.length));
  siblings.splice(insertIndex, 0, { ...dragged, parentId: newParentId });
  next[newParentId] = siblings.map((b, i) => ({ ...b, position: i }));

  return { next, oldParentId, newParentId };
}

export function resolveDropTarget(
  target: SidebarDropTarget,
  childrenByParent: Record<string, BlockRecord[]>,
): { parentId: string; index: number } {
  if (target.kind === 'before') {
    return { parentId: target.parentId, index: target.index };
  }
  const kids = sortByPosition(childrenByParent[target.parentId] ?? []);
  return { parentId: target.parentId, index: kids.length };
}

export async function moveSidebarBlock(
  blockId: string,
  target: SidebarDropTarget,
  childrenByParent: Record<string, BlockRecord[]>,
): Promise<{ oldParentId: string; newParentId: string; siblings: BlockRecord[] }> {
  const dragged = await fetchBlock(blockId);
  const oldParentId = dragged.parentId;
  if (!oldParentId) {
    throw new Error('Cannot move a top-level workspace page');
  }

  const { parentId: newParentId, index: targetIndex } = resolveDropTarget(target, childrenByParent);

  if (blockId === newParentId || isDescendantInTree(newParentId, blockId, childrenByParent)) {
    throw new Error('Cannot move a page into itself or its descendants');
  }

  const existing = sortByPosition(childrenByParent[newParentId] ?? []);
  const siblings = existing.filter((b) => b.id !== blockId);
  const insertIndex = Math.max(0, Math.min(targetIndex, siblings.length));
  siblings.splice(insertIndex, 0, { ...dragged, parentId: newParentId });

  const updates = siblings.map((b, i) => ({ id: b.id, position: i }));

  if (oldParentId !== newParentId) {
    await reparentBlock(blockId, { parentId: newParentId, position: insertIndex });
  }

  await reorderBlocks(updates);

  return { oldParentId, newParentId, siblings };
}
